import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { FinancialAccountType } from "@common/types/financialAccount";
import { FINANCIAL_ACCOUNT_TYPE_LABEL } from "@common/constants";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import CheckingIcon from "@icons/accountTypes/Checking.svg?react";
import SavingsIcon from "@icons/accountTypes/Savings.svg?react";
import CreditCardIcon from "@icons/accountTypes/CreditCard.svg?react";
import LoanIcon from "@icons/accountTypes/Loan.svg?react";
import InvestmentIcon from "@icons/accountTypes/Investment.svg?react";
import PaymentProcessorIcon from "@icons/accountTypes/PaymentProcessor.svg?react";
import CashIcon from "@icons/accountTypes/Cash.svg?react";
import OtherIcon from "@icons/accountTypes/Other.svg?react";
import { AccountsTableRow } from "../accountsView.types";
import styles from "../accountsView.module.scss";

const FINANCIAL_ACCOUNT_TYPE_ICON: Record<
    FinancialAccountType,
    React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
    [FinancialAccountType.CHECKING]: CheckingIcon,
    [FinancialAccountType.SAVINGS]: SavingsIcon,
    [FinancialAccountType.CREDIT_CARD]: CreditCardIcon,
    [FinancialAccountType.LOAN]: LoanIcon,
    [FinancialAccountType.LINE_OF_CREDIT]: LoanIcon,
    [FinancialAccountType.INVESTMENT]: InvestmentIcon,
    [FinancialAccountType.PAYMENT_PROCESSOR]: PaymentProcessorIcon,
    [FinancialAccountType.CASH]: CashIcon,
    [FinancialAccountType.OTHER]: OtherIcon,
};

export const AccountTypeCell = memo(
    ({ row }: CellContext<ViewTableFeatures, AccountsTableRow, unknown>) => {
        const rowData = row.original;
        if (!rowData.account) {
            return <ViewTableCell />;
        }

        const typeLabel =
            FINANCIAL_ACCOUNT_TYPE_LABEL[rowData.account.financialType];
        const Icon = FINANCIAL_ACCOUNT_TYPE_ICON[rowData.account.financialType];

        const isDisabled = rowData.account.disabled;

        return (
            <ViewTableCell>
                <span
                    className={
                        isDisabled
                            ? styles.accountTypeDisabled
                            : styles.accountType
                    }
                >
                    {Icon && (
                        <Icon
                            width={16}
                            height={16}
                            className="icon-color-current"
                        />
                    )}
                    {typeLabel}
                </span>
            </ViewTableCell>
        );
    },
);

AccountTypeCell.displayName = "AccountTypeCell";
